import React from 'react'
import { isEmpty } from 'lodash'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

// Boolean bit fields are filtered by their true/false value (a separate
// boolean_fields param), not by taxonomy options like pick_list fields.
export const isBooleanBitField = (field) => field.data_type === 'boolean'

export const BOOLEAN_FIELD_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

// The playbook cids currently filtered on (empty when no playbook filter).
export const getSelectedPlaybookCids = (filters) =>
  (filters.playbooks ?? []).map((playbook) => playbook.value)

// A bit-field option applies when no playbook is filtered, or when it's
// configured on one of the filtered playbooks (each option carries the
// playbook_cids it resolves on).
export const optionAppliesToPlaybooks = (option, selectedPlaybookCids) =>
  isEmpty(selectedPlaybookCids) ||
  (option.playbook_cids ?? []).some((cid) => selectedPlaybookCids.includes(cid))

// A bit field's react-select options, flagged by whether they apply to the
// filtered playbooks and sorted so applicable ones come first. The same option
// name can come from several taxonomies (one per playbook), each with its own
// category_uuid; collapse those into a single entry that carries every uuid, so
// it shows once and selecting it filters on all of them. `value` joins the
// uuids to stay a stable, unique react-select key.
export const buildBitFieldOptions = (field, selectedPlaybookCids) => {
  const byName = new Map()
  field.options.forEach((option) => {
    const existing = byName.get(option.name)
    if (existing) {
      existing.categoryUuids.push(option.category_uuid)
      existing.playbook_cids.push(...(option.playbook_cids ?? []))
    } else {
      byName.set(option.name, {
        label: option.name,
        categoryUuids: [option.category_uuid],
        playbook_cids: [...(option.playbook_cids ?? [])],
      })
    }
  })

  return [...byName.values()]
    .map((entry) => ({
      value: entry.categoryUuids.join('|'),
      label: entry.label,
      categoryUuids: entry.categoryUuids,
      applicable: optionAppliesToPlaybooks(entry, selectedPlaybookCids),
    }))
    .sort((first, second) => Number(second.applicable) - Number(first.applicable))
}

export const isBitFieldOptionDisabled = (option) => !option.applicable

// Non-applicable options render greyed with a tooltip; applicable ones show
// their plain label. The disabled option sets pointer-events: none
// (.option-component-disabled), so re-enable them on the label or the tooltip
// never sees a hover.
export const renderBitFieldOption = (option) =>
  option.applicable ? (
    option.label
  ) : (
    <span className="notetaker-filter-option">
      <InteractiveTooltip content="Applicable playbook is filtered">
        <span>{option.label}</span>
      </InteractiveTooltip>
    </span>
  )
